<template>
  <div class="configuration-view scroll-contents">
    <header>
      <div class="title">
        <h1>設定</h1>
        <button class="add-account-btn" @click="addAccount(false)">アカウント追加＋</button>
      </div>
      <div class="advance_link">
        <a :href="advanceConfigURL" target="_blank">
          高度な設定
          <img src="/images/outer_link_icon.svg" />
        </a>
      </div>
    </header>
    <div class="configure-body">
      <div 
        class="configure-user-column"
        v-for="user in availableUsers"
        :key="'confusr' + user.id"
        >
        <div class="configure-user-block">
          <div class="head-block">
            <!-- display icon image -->
            <div class="name-column">
              <user-portrate :target="user" :displaySize="'middle'"/>
              <div class="name">{{ user.name }}</div>
              <span class="signin-marker" v-if="user.id === currentUser.id">アクティブ</span>
            </div>
            <div class="controlls">
              <div class="user-state" :class="userStateColor(user)"><span>{{ userStateText(user) }}</span></div>
              <div class="controll-column">
                <img src="/images/edit_icon.svg" @click="editUser(user)" />
                <img src="/images/trash_icon.svg" @click="deleteUser(user)" />
                <button class="change-password-btn" v-if="user.own_signin" @click="changePassword(user)">パスワード変更</button>
              </div>
            </div>
          </div>
          <div class="basic-block">
            <div class="label-value-column">
              <div class="label">ナラティブフォンID</div>
              <div class="value" v-if="user.narrative_phone_id">
                {{ user.narrative_phone_id }}
                <button @click="copyToClipboard(user.narrative_phone_id)">コピー</button>
              </div>
              <div class="value notify" v-if="!user.narrative_phone_id">ナラティブフォンIDがまだ発行されていません。<br/>Zoom連携後に発行されます。</div>
            </div>
            <div class="label-value-column">
              <div class="label">アカウント名</div>
              <div class="value">{{ user.account_name }}</div>
            </div>
            <div class="label-value-column">
              <div class="label">表示名</div>
              <div class="value">{{ user.name }}</div>
            </div>
            <div class="label-value-column">
              <div class="label">説明</div>
              <div class="value" v-html="descriptionHTML(user)"></div>
            </div>
            <div class="label-value-column">
              <div class="label">メールアドレス</div>
              <div class="value">{{ user.email }}</div>
            </div>
          </div>
          <div class="narrativebook-block" v-if="user.nb_id">
            <h2>ナラティブブック</h2>
            <div class="label-value-column">
              <div class="label">NBID</div>
              <div class="value">{{ user.nb_id }}</div>
            </div>
            <div class="action-column" v-if="user.nb_id">
              <a :href="narrativeAccessURL(user)" target="_blank">
                ナラティブブックを開く
                <img src="/images/outer_link_icon.svg" />
              </a><br/>
            </div>
          </div>
          <div class="narrativebook-block" v-if="!user.nb_id">
            <h2>ナラティブブック</h2>
            <div class="label-value-column">
              <div class="label">状態</div>
              <div class="value">未連携</div>
            </div>
            <div class="action-column">
              <a @click="connectNarrativebook(user)">
                ナラティブブックと連携する
                <img src="/images/outer_link_icon.svg" />
              </a>
            </div>
          </div>
          <div class="zoom-block">
            <h2>Zoomアカウント</h2>
            <div class="label-value-column">
              <div class="label">{{ zoomAccount(user).label }}</div>
              <div class="value">{{ zoomAccount(user).value }}</div>
            </div>
            <div class="action-column">
              <a @click="contactZoom(user)">
                {{ zoomConnectionText(user) }}
                <img src="/images/outer_link_icon.svg" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="version-display">Current Version: API: {{ apiVersion }} / FrontEnd: {{ versionInfo }}</div>
    </div>
    <user-edit-modal
      :user="targetUser"
      :modalFrameDisplay="userEditModalDisplay"
      v-on:cancelEditEvent="cancelEditUser"
      v-on:submitEditEvent="submitEditUser"
      />
    <password-edit-modal
      :modalDisplay="userEditPasswordModalDisplay"
      v-on:passwordUpdateEvent="passwordUpdate"
      v-on:cancelEvent="passwordEditCancel"
      />
    <confirm-dialog
      :showDialog="confirmDialogVisible"
      :title="confirmDialogTitle"
      :message="confirmDialogMessage"
      :dialogType="confirmDialogType"
      v-on:negativeResultEvent="confirmFinishNegative"
      v-on:positiveResultEvent="confirmFinishPositive"
      />
  </div>
</template>

<script>
import { ApiVersionInfo } from '@/api'
import { mapState, mapActions } from 'vuex'

import UserPortrate from '@/components/appView/common/UserPortrate.vue'
import UserEditModal from '@/components/appView/config/UserEditModal.vue'
import PasswordEditModal from '@/components/appView/config/PasswordEditModal.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'

import ConfirmDialogControll from '@/mixin/ConfirmDialogControll'

import * as DIALOG_TYPE from '@/assets/constants/dialogType.js'
import { zoomConnected, zoomAccountDisplay } from '@/lib/zoomAccountDisplay.mjs'

export default {
  mixins: [ ConfirmDialogControll ],

  name: 'ConfigurationView',

  components: {
    UserPortrate,
    UserEditModal,
    PasswordEditModal,
    ConfirmDialog,
  },

  data () {
    return {
      targetUser: null,
      userEditModalDisplay: false,
      userEditPasswordModalDisplay: false,
      apiVersion: '',
      presentUser: null,
    }
  },

  mounted() {
    this.loadApiVersionInfo()
    this.checkCurrentUserAvailable()
  },

  computed: {
    ...mapState({
      currentUser: state => state.user.currentUser,
      currentDevice: state => state.user.currentDevice,
      availableUsers: state => state.user.users,
    }),

    advanceConfigURL () {
      return window.location.protocol + '//' + window.location.host + '/user_config'
    },

    versionInfo () {
      return process.env.VUE_APP_VERSION_INFO
    }
  },

  methods: {
    ...mapActions({
      fetchCurrentSignin: 'user/fetchCurrentSignin',
      fetchAvailableUsers: 'user/fetchAvailableUsers',
      updateUser: 'user/update',
      updateUserPassword: 'user/updatePassword',
      switchAccount: 'user/switchAccount',
      destoyUser: 'user/destroy',
      deleteDeviceRelation: 'user/deleteDeviceRelation',
      signout: 'user/signout',
    }),

    userStateColor (user) {
      let state = ''
      switch (user.user_state) {
        case 0:
          state = 'before-validation'
          break
        case 1:
          state = 'in-progress'
          break
        case 2:
          state = 'available'
          break
        case 3:
          state = 'suspend'
          break
        default:
          state = 'none'
          break
      }
      if (this.zoomConnected(user)) {
        return state
      } else {
        return 'before-validation'
      }
    },

    userStateText (user) {
      if (user.user_state !== 2 || (user.user_state === 2 && this.zoomConnected(user))) {
        return user.user_state_jp
      } else {
        return '登録中(Zoom未連携)'
      }
    },

    currentPushTarget () {
      return this.currentDevice ? this.currentDevice.push_target : undefined
    },

    zoomConnected (user) {
      return zoomConnected(user, this.currentPushTarget())
    },

    // ID ではなく Zoom のメールを出す（NP-USR-FEAT-007）
    zoomAccount (user) {
      return zoomAccountDisplay(user, this.currentPushTarget())
    },

    zoomConnectionText (user) {
      if (this.zoomConnected(user)) {
        return 'Zoomに再接続する'
      } else {
        return 'Zoomに接続する'
      }
    },

    descriptionHTML (user) {
      if (user.description) {
        return user.description.replace(/\n/gi, '<br />')
      }
      return ''
    },

    loadApiVersionInfo () {
      ApiVersionInfo.fetch().then(res => {
        console.log(res.data)
        this.apiVersion = res.data.version
      })
    },

    checkCurrentUserAvailable () {
      console.log('checkCurrentUserAvailable')
      if (this.currentUser.user_state !== 2 || 
        ((this.currentDevice.push_target === 0 && !this.currentUser.ios_zoom_available) 
        || (this.currentDevice.push_target === 1 && !this.currentUser.android_zoom_available))
      ) {
        console.log('currentUser is not available.....')
        // current user not available
        // check switchable user
        let switchableUser = null
        for(const user of this.availableUsers) {
          console.log(user)
          if (user.user_state === 2 &&
            ((this.currentDevice.push_target === 0 && user.ios_zoom_available) 
            || (this.currentDevice.push_target === 1 && user.android_zoom_available))
          ) {
            console.log('find...')
            switchableUser = user
          }
        }
        if (switchableUser) {
          console.log('find switchableUser.....')
          this.switchAccount(switchableUser.account_name)
        }
      }
    },

    addAccount (resetOld) {
      // TODO: add account impremetation
      let query = {
        phone_device_description: this.currentDevice.phone_device_description,
        phone_device_token: this.currentDevice.phone_device_token,
        push_target: this.currentDevice.push_target,
        return_path: '/config',
      }
      if (resetOld) {
        query = {
          phone_device_description: this.currentDevice.phone_device_description,
          phone_device_token: this.currentDevice.phone_device_token,
          push_target: this.currentDevice.push_target,
          return_path: '/config',
          reset_old_relation: 'YES',
          device_cleared: 'YES'
        }
      }
      this.$router.push({ 
        path: '/user/new_user_with_device', 
        query: query
      })
    },

    switchBackInvalidSignin () {
      if (this.currentUser.state !== 2 && this.presentUser) {
        this.switchAccount(this.presentUser.account_name)
        this.presentUser = null
      }
    },

    editUser (user) {
      if (this.currentUser.id === user.id) {
        this.startEditUser(user)
      } else {
        this.showConfirmDialog(
          "アカウントの切替", 
          'アカウントの編集を行うには、アクティブなアカウントを切り替える必要があります。\n' + user.account_name + 'にアクティブなアカウントを切り替えて編集を行います。',
          DIALOG_TYPE.EXECUTE, 
          () => { this.startEditUser(user) }
        )
      }
    },

    async startEditUser (user) {
      this.presentUser = this.currentUser
      await this.switchAccount(user.account_name)
      this.targetUser = user
      this.userEditModalDisplay = true
    },

    cancelEditUser () {
      this.switchBackInvalidSignin()
      this.userEditModalDisplay = false
      this.targetUser = null
    },

    submitEditUser (formData) {
      console.log('event receive, gonna request')
      this.updateUser(formData).then(res => {
        console.log(res)
        let message = 'アカウント情報を更新しました。'
        if (res.data.message && res.data.message.length > 0) {
          message += '\n' + res.data.message
        }
        this.switchBackInvalidSignin()
        this.userEditModalDisplay = false
        this.targetUser = null
        this.showMessageDialog('アカウント情報の更新', message)
      }).catch(error => {
        console.error(error)
        this.switchBackInvalidSignin()
        this.userEditModalDisplay = false
        this.targetUser = null
        this.showNotifyDialog('アカウント情報更新失敗', 'アカウント情報の更新に失敗しました。')
      })
    },

    deleteUser (user) {
      this.showConfirmDialog(
        "アカウントの削除", 
        'アカウント ' + user.name + '(' + user.account_name + ')をこのデバイスから削除します。\n本当によろしいですか？',
        DIALOG_TYPE.EXECUTE, 
        () => { this.executeDeleteUser(user) }
      )
    },

    async executeDeleteUser (user) {
      let lastUser = false
      if (this.availableUsers.length == 1) {
        lastUser = true
      }
      console.log('gonna switch account......')
      this.switchAccount(user.account_name).then(() => {
        this.deleteDeviceRelation(this.currentDevice.phone_device_id).then(() => {
          if (lastUser) {
            this.showConfirmDialog(
              "アカウントを削除しました", 
              'アカウント ' + user.name + '(' + user.account_name + ')をこのデバイスから削除しました\n完全にサーバーから情報を削除するには、「高度な設定」からログインしてアカウントの削除を行ってください。\n現在、利用可能なユーザーが一人もいません。\n新規ユーザーをインポートするか、作成してください。',
              DIALOG_TYPE.NOTIFY, 
              () => { 
                this.signout()
                this.addAccount(true) 
              }
            )
          } else {
            this.showNotifyDialog("アカウントを削除しました", 'アカウント ' + user.name + '(' + user.account_name + ')をこのデバイスから削除しました\n完全にサーバーから情報を削除するには、「高度な設定」からログインしてアカウントの削除を行ってください。')
            this.fetchAvailableUsers().then(() => {
              let switchTarget = this.availableUsers[0]
              for(const sUser of this.availableUsers) {
                if (sUser.state == 2) {
                  switchTarget = sUser
                }
              }
              if (switchTarget) {
                this.switchAccount(switchTarget.account_name)
              } else {
                this.signout()
                this.addAccount(true) 
              }
            })
          }
        }).catch(error => {
          console.error(error)
          this.showNotifyDialog("アカウント削除に失敗しました", 'アカウントの削除に失敗しました。')
        })
      }).catch(error => {
        console.error(error)
        this.showNotifyDialog("アカウント削除に失敗しました", 'アカウントの削除に失敗しました。')
      })
    },

    changePassword (user) {
      if (this.currentUser.id === user.id) {
        this.startChangePassword(user)
      } else {
        this.showConfirmDialog(
          "アカウントの切替", 
          'アカウントのパスワード変更を行うには、アクティブなアカウントを切り替える必要があります。\n' + user.account_name + 'にアクティブなアカウントを切り替えてパスワード変更を行います。',
          DIALOG_TYPE.EXECUTE, 
          () => { this.startChangePassword(user) }
        )
      }
    },

    async startChangePassword (user) {
      this.presentUser = this.currentUser
      await this.switchAccount(user.account_name)
      this.userEditPasswordModalDisplay = true
    },

    passwordUpdate (password, passwordConfirmation) {
      this.updateUserPassword({ password: password, passwordConfirmation: passwordConfirmation }).then(res => {
        console.log(res)
        this.switchBackInvalidSignin()
        this.userEditPasswordModalDisplay = false
        this.showNotifyDialog('パスワード更新', 'パスワードを更新しました。')
      }).catch(error => {
        console.error(error)
        this.switchBackInvalidSignin()
        this.userEditPasswordModalDisplay = false
        this.showNotifyDialog('パスワード更新失敗', 'パスワードの更新に失敗しました。')
      })
    },

    passwordEditCancel () {
      this.switchBackInvalidSignin()
      this.userEditPasswordModalDisplay = false
    },

    narrativeAccessURL (user) {
      console.log(user)
      return process.env.VUE_APP_NARRATIVEBOOK_HOST
    },

    connectNarrativebook (user) {
      if (this.currentUser.id === user.id) {
        this.startConnectNarrativebook(user)
      } else {
        this.showConfirmDialog(
          "アカウントの切替", 
          'アカウントのナラティブブック連携を行うには、アクティブなアカウントを切り替える必要があります。\n' + user.account_name + 'にアクティブなアカウントを切り替えてナラティブブック連携を行います。',
          DIALOG_TYPE.EXECUTE, 
          () => { this.startConnectNarrativebook(user) }
        )
      }
    },

    async startConnectNarrativebook (user) {
      console.log(user)
      this.presentUser = this.currentUser
      await this.switchAccount(user.account_name)
      const nbAuthURL = location.protocol 
        + '//' 
        + location.host 
        + '/api/user/contact_narrative_auth' 
        + '?phone_device_token=' + encodeURIComponent(this.currentDevice.phone_device_token)
        + '&phone_device_description=' + encodeURIComponent(this.currentDevice.phone_device_description)
        + '&push_target=' + encodeURIComponent(this.currentDevice.push_target)
        + '&non_related_user=' + this.currentUser.id
      this.$native.requestSessionReset({ loginId: user.account_name, requestUrl: nbAuthURL })
    },

    contactZoom (user) {
      console.log(user)
      if (this.currentUser.id === user.id) {
        this.startContactZoom(user)
      } else {
        this.showConfirmDialog(
          "アカウントの切替", 
          'アカウントのZoom接続を行うには、アクティブなアカウントを切り替える必要があります。\n' + user.account_name + 'にアクティブなアカウントを切り替えてZoom接続を行います。',
          DIALOG_TYPE.CONFIRM, 
          () => { this.startContactZoom(user) }
        )
      }
    },

    // TODO: switch account notification
    startContactZoom (user) {
      this.presentUser = this.currentUser
      this.switchAccount(user.account_name).then(res => {
        console.log(res)
        let targetURL = ''
        if (this.currentDevice.push_target === 0) {
          targetURL = location.protocol 
            + '//' 
            + location.host 
            + '/api/user/contact_zoom_auth?retry_type=ios'
        } else if (this.currentDevice.push_target === 1) {
          targetURL = location.protocol 
            + '//' 
            + location.host 
            + '/api/user/contact_zoom_auth?retry_type=android'
        }
        console.log('zoom auth url: ' + targetURL)
        this.$native.requestSessionReset({ loginId: this.currentUser.account_name, requestUrl: targetURL })
      }).catch(error => {
        console.error(error)
        this.showNotifyDialog('Zoom接続失敗', 'Zoomへの接続に失敗しました。')
      })
    },

    copyToClipboard (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showNotifyDialog('コピー完了', 'ナラティブフォンIDをクリップボードにコピーしました。')
      }).catch(err => {
        console.error('Failed to copy: ', err)
        this.showNotifyDialog('コピー失敗', 'ナラティブフォンIDのコピーに失敗しました。')
      })
    }

  }

}
</script>

<style lang="scss" scoped>
@import '@/assets/css/global.scss';
@import '@/assets/css/mediaQuery.scss';
@import '@/assets/css/colorTable.scss';

.configuration-view {
  margin: 0;
  height: 100%;
  header {
    display: block;
    border-bottom: 1px solid #aaa;
    height: 76px;
    .title {
      display: flex;
      justify-content: space-between;
      h1 {
        margin: 0;
        font-weight: normal;
        font-size: 22pt;
      }
      .add-account-btn {
        padding: 0 17px;
        background-color: $normal-btn-color;
        color: $normal-btn-text-color;
        font-weight: bold;
        border-color: $normal-btn-color;
        height: 34px;
        border-radius: 17px;
      }
    }
    .advance_link {
      margin-top: 10px;
      padding: 5px 10px;
      text-align: right;
      a {
        color: #666;
        border-bottom: 1px solid #666;
        font-size: 13pt;
        text-decoration: none;
        img {
          height: 12pt;
        }
      }
    }
  }
  .configure-body {
    height: calc(100% - 107px);
    overflow: auto;
  }
  .configure-user-column {
    border-bottom: 1px solid #aaa;
    .configure-user-block {
      padding: 15px;
      .head-block {
        border-bottom: 1px solid #aaa;
        .name-column {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          .user-portrate {
            margin: 0;
          }
          .name {
            margin: 10px;
            font-size: 16pt;
          }
          .signin-marker {
            font-size: 12px;
            color: $success-color;
          }
        }
        .controlls {
          margin: 10px 0;
          display: flex;
          justify-content: space-between;
          .user-state {
            // height: 10pt;
            display: flex;
            align-items: center;
            padding: 3px 5px;
            font-size: 10pt;
            border: 1px solid #aaa;
            &.before-validation {
              background-color: #fff;
              color: $warning-color;
              border-color: $warning-color;
            }
            &.in-progress {
              background-color: #fff;
              color: $warning-color;
              border-color: $warning-color;
              font-size: 9pt;
            }
            &.available {
              background-color: #fff;
              color: $success-color;
              border-color: $success-color;
            }
            &.suspend {
              background-color: #fff;
              color: $danger-color;
              border-color: $danger-color;
            }
          }
          .controll-column {
            display: flex;
            align-items: center;
            img {
              margin: 0 10px;
              height: 30px;
            }
            .change-password-btn {
              margin: 0 10px;
              padding: 5px 10px;
              border-radius: 5px;
              border: 1px solid $normal-btn-color;
              background-color: $normal-btn-color;
              color: $normal-btn-text-color;
              font-size: 0.9rem;
            }
          }
        }
      }
      .label-value-column {
        padding: 5px 0;
        display: flex;
        flex-wrap: wrap;
        .label {
          min-width: 30%;
          text-align: right;
          font-size: 10pt;
          font-weight: bold;
        }
        .value {
          padding-left: 10px;
          font-size: 11pt;
          &.notify {
            color: $danger-color;
          }
        }
      }
      .action-column {
        text-align: right;
        a {
          color: #666;
          border-bottom: 1px solid #666;
          font-size: 13pt;
          text-decoration: none;
          img {
            height: 12pt;
          }
        }
      }
      .basic-block {
        padding: 10px 0;
        border-bottom: 1px solid #aaa;
      }
      .narrativebook-block {
        padding: 10px 0;
        border-bottom: 1px solid #aaa;
        h2 {
          margin: 0;
          font-size: 13pt;
          font-weight: bold;
        }
      }
      .zoom-block {
        padding: 10px 0;
        h2 {
          margin: 0;
          font-size: 13pt;
          font-weight: bold;
        }
      }
    }
  }
  .version-display {
    padding: 3px 5px;
    text-align: right;
    font-size: 12px;
    color: #aaa;
  }
}
</style>